import { useId, useLayoutEffect, useRef, useState } from "react"
import type { ButtonHTMLAttributes, ReactNode } from "react"
import { Badge, Text, Textarea } from "@smooth/design-system-components"
import type { TextareaProps } from "@smooth/design-system-components"
import { UNSET_SHORT_NAME, isUnsetTerminal, terminalShortLabel } from "../../terminals"

// AIドラフト検証の画面だけで使う小さな部品。design system に相当する部品が無いものだけをここに置き、
// 色・文字サイズは semantic トークン由来の Tailwind クラスだけで指定する

/** 対応 ID（A, F, HUMAN_REVIEW 等）の標識。期待と食い違う観測だけ赤にする */
export function TerminalBadge({ id, mismatch = false }: { id: string; mismatch?: boolean }) {
  return <Badge tone={mismatch ? "error" : "accent"}>{terminalShortLabel(id)}</Badge>
}

/** 対応 ID と表示名を並べる */
export function TerminalName({
  id,
  terminals,
  mismatch,
}: {
  id: string
  terminals: Record<string, string>
  mismatch?: boolean
}) {
  return (
    <span className="inline-flex items-baseline gap-2">
      <TerminalBadge id={id} mismatch={mismatch} />
      <Text as="span">{terminals[id] ?? (isUnsetTerminal(id) ? UNSET_SHORT_NAME : "未定義")}</Text>
    </span>
  )
}

/**
 * 画面内の操作をするテキストリンク風ボタン（生成履歴の日時など）。
 * DS の Link は href 必須の <a> なので、画面遷移しない操作にはこちらを使う
 */
export function TextButton({ className, ...rest }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={[
        "text-body text-text-accent underline underline-offset-2",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring",
        "disabled:cursor-not-allowed disabled:text-text-muted disabled:no-underline",
        className ?? "",
      ].join(" ")}
      {...rest}
    />
  )
}

/** 罫線で区切るだけの節。箱（背景・角丸）は付けない */
export function Section({
  label,
  children,
  className,
}: {
  label?: string
  children: ReactNode
  className?: string
}) {
  return (
    <section
      aria-label={label}
      className={`flex flex-col gap-3 border-b border-border-default py-5 ${className ?? ""}`}
    >
      {children}
    </section>
  )
}

/** 編集欄の並びに置く読み取り専用の値。AI には渡るが、読み込んだ値のままでよい項目に使う（入力欄にしない） */
export function ReadOnlyField({
  label,
  ariaLabel,
  hint,
  error,
  children,
}: {
  label: string
  /** 見出しより詳しい名前（どの行の値か）。指定すると見出しの代わりにこれを値の名前にする */
  ariaLabel?: string
  hint?: string
  /** 表示だけの値が保存できない値のときの理由（欄で直せないので、直し方も書く） */
  error?: string
  children: ReactNode
}) {
  const id = useId()
  return (
    <div className="flex flex-col gap-2">
      <span id={id} className="text-caption font-bold text-text-default">
        {label}
      </span>
      {/* <output> は live region（role="status"）で、値が変わるたびに読み上げられるので使わない */}
      <p
        aria-labelledby={ariaLabel ? undefined : id}
        aria-label={ariaLabel}
        className="whitespace-pre-wrap py-1 text-body text-text-muted [overflow-wrap:anywhere]"
      >
        {children}
      </p>
      {hint ? <p className="text-caption text-text-muted">{hint}</p> : null}
      {error ? (
        <p role="alert" className="text-caption text-text-error">
          {error}
        </p>
      ) : null}
    </div>
  )
}

/**
 * 中身に合わせて高さが伸びる複数行の入力欄（内側でスクロールさせない）。
 * DS の Textarea は高さを rows に委ねるので、入力のたびに中身の高さへ合わせる
 */
export function AutoGrowTextarea({
  minRows = 3,
  value,
  ...rest
}: Omit<TextareaProps, "rows" | "value"> & { minRows?: number; value: string }) {
  const ref = useRef<HTMLTextAreaElement>(null)
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    element.style.height = "auto"
    // 枠線の分を足さないと、最後の行の分だけ内側でスクロールする
    const border = element.offsetHeight - element.clientHeight
    element.style.height = `${element.scrollHeight + border}px`
  }, [value])
  return (
    <Textarea
      ref={ref}
      rows={minRows}
      value={value}
      className="resize-none overflow-hidden"
      {...rest}
    />
  )
}

/** 改行を保ったまま本文を出す（DS の Text は className を受けないので外側で white-space を当てる） */
export function MultilineText({ children }: { children: ReactNode }) {
  return (
    <div className="whitespace-pre-wrap [overflow-wrap:anywhere]">
      <Text>{children}</Text>
    </div>
  )
}

/**
 * 長くなりうる複数行の文。最初の4行だけ出し、はみ出すときだけ「続きを見る」／「閉じる」で開閉する
 * （読み込んだケースのメモは数十行になり、下の生成・生成結果を押し出すため）
 */
export function ClampedText({ children }: { children: string }) {
  const id = useId()
  const ref = useRef<HTMLParagraphElement>(null)
  const [open, setOpen] = useState(false)
  const [overflowing, setOverflowing] = useState(false)
  useLayoutEffect(() => {
    const element = ref.current
    if (!element || open) return
    const measure = () => setOverflowing(element.scrollHeight > element.clientHeight + 1)
    measure()
    if (typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [children, open])
  return (
    <div className="flex flex-col items-start gap-1">
      <p
        id={id}
        ref={ref}
        className={`whitespace-pre-wrap text-body text-text-default [overflow-wrap:anywhere] ${
          open ? "" : "line-clamp-4"
        }`}
      >
        {children}
      </p>
      {(overflowing || open) && (
        <button
          type="button"
          aria-expanded={open}
          aria-controls={id}
          onClick={() => setOpen((value) => !value)}
          className="text-caption font-bold text-text-accent hover:opacity-70"
        >
          {open ? "閉じる" : "続きを見る"}
        </button>
      )}
    </div>
  )
}

/** 成功・進行中の知らせ */
export function StatusMessage({ children }: { children: ReactNode }) {
  return (
    <div role="status">
      <Text>{children}</Text>
    </div>
  )
}

/** 失敗の知らせ。赤文字だけにして箱は増やさない */
export function AlertMessage({ children }: { children: ReactNode }) {
  return (
    <div role="alert">
      <Text tone="error">{children}</Text>
    </div>
  )
}

/** 開閉できる補足（生成時のデータ・プロンプト等）。中身は等幅で折り返す */
export function Disclosure({
  summary,
  children,
  defaultOpen = false,
}: {
  summary: string
  children: string
  defaultOpen?: boolean
}) {
  return (
    <details open={defaultOpen} className="border-t border-border-default">
      <summary className="cursor-pointer py-2 text-body font-bold text-text-default">
        {summary}
      </summary>
      <pre className="max-h-[500px] overflow-auto whitespace-pre-wrap bg-bg-muted p-3.5 font-mono text-caption text-text-default [overflow-wrap:anywhere]">
        {children}
      </pre>
    </details>
  )
}
